/**
 * ClientCreateForm - Simple form for creating new clients
 * TASK-209: Focused creation form without selection UI
 */

import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import type { Client } from '../../types/client';

interface ClientCreateFormProps {
  onClientCreated: (client: Client) => void;
}

export const ClientCreateForm: React.FC<ClientCreateFormProps> = ({ onClientCreated }) => {
  const { createClient, clientError } = useOnboarding();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'individual' as 'individual' | 'corporate',
    jurisdiction: 'CH',
    is_prospect: false,
  });

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsCreating(true);

    try {
      const client = await createClient(formData);
      if (client) {
        onClientCreated(client);
      } else {
        // createClient swallowed the throw and parked the server's reason on the context.
        // It is rendered below; reading it here would get the previous render's value.
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error Display — `error` covers a thrown failure, `clientError` the reason the
          context captured when createClient returned null. */}
      {(error || clientError) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error || clientError}</p>
        </div>
      )}

      {/* Name Field - Required */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Client Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="John Doe"
          disabled={isCreating}
        />
      </div>

      {/* Email Field - Required */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="john@example.com"
          disabled={isCreating}
        />
      </div>

      {/* Phone Field - Optional */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone
        </label>
        <input
          type="tel"
          id="phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="+41 79 123 45 67"
          disabled={isCreating}
        />
      </div>

      {/* Client Type */}
      <div>
        <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
          Client Type
        </label>
        <select
          id="type"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as 'individual' | 'corporate' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={isCreating}
        >
          <option value="individual">Individual</option>
          <option value="corporate">Corporate</option>
        </select>
      </div>

      {/* Jurisdiction */}
      <div>
        <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 mb-1">
          Jurisdiction
        </label>
        <select
          id="jurisdiction"
          value={formData.jurisdiction}
          onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={isCreating}
        >
          <option value="CH">Switzerland (CH)</option>
          <option value="EU">European Union (EU)</option>
          <option value="US">United States (US)</option>
          <option value="UK">United Kingdom (UK)</option>
          <option value="OTHER">Other</option>
        </select>
      </div>

      {/* Prospect Checkbox */}
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id="is_prospect"
          checked={formData.is_prospect}
          onChange={(e) => setFormData({ ...formData, is_prospect: e.target.checked })}
          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          disabled={isCreating}
        />
        <label htmlFor="is_prospect" className="text-sm text-gray-700">
          <span className="font-medium">Mark as Prospect</span>
          <p className="text-xs text-gray-500 mt-0.5">
            Check this if the client is not yet onboarded
          </p>
        </label>
      </div>

      {/* Submit Button */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={isCreating || !formData.name || !formData.email}
          className="w-full px-4 py-2 bg-kielis-navy text-white font-medium rounded-md hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isCreating ? 'Creating Client...' : 'Create Client'}
        </button>
      </div>
    </form>
  );
};
